import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import {
  bookingsData, getRoomById, getStructureById
} from "@/data/platformData";
import { getPatientById, getServiceById, serviceBundlesData, doctorsData } from "@/data/mockData";
import {
  ClipboardList, ArrowLeft, CalendarClock, Bell, Trash2, Plus, User, MapPin
} from "lucide-react";

const PHARMACY_ID = "ph1";

const statusBadge = (s) => ({
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  completed: "bg-sky-50 text-sky-700 border-sky-200",
  rescheduled: "bg-indigo-50 text-indigo-700 border-indigo-200"
}[s] || "bg-slate-50 text-slate-700 border-slate-200");

export const ManagerBookingsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(
    bookingsData.filter(b => b.pharmacyId === PHARMACY_ID)
  );
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDate, setMoveDate] = useState("");
  const [moveTime, setMoveTime] = useState("");

  const cancelBooking = (id) => {
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status: "cancelled" } : b));
    toast.success("Prenotazione annullata", { description: "Notifica email/SMS inviata al paziente." });
  };

  const openMove = (b) => {
    setMoveTarget(b);
    setMoveDate(b.date);
    setMoveTime(b.time);
  };

  const confirmMove = () => {
    setBookings(prev => prev.map(b => b.id === moveTarget.id ? { ...b, status: "rescheduled", date: moveDate, time: moveTime } : b));
    toast.success("Prenotazione spostata", { description: `${moveDate} alle ${moveTime} · notifica inviata.` });
    setMoveTarget(null);
  };

  const renderBookingCard = (b) => {
    const patient = getPatientById(b.patientId);
    const room = getRoomById(b.diagnosticRoomId);
    const structure = b.structureId ? getStructureById(b.structureId) : null;
    const specialist = b.specialistId ? doctorsData.find(d => d.id === b.specialistId) : null;
    const services = b.bundleId
      ? [serviceBundlesData.find(x => x.id === b.bundleId)?.name].filter(Boolean)
      : b.serviceIds.map(sid => getServiceById(sid)?.name).filter(Boolean);

    return (
      <Card key={b.id} data-testid={`booking-card-${b.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <p className="font-semibold">{patient ? patient.name : `Paziente ${b.patientId}`}</p>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                <CalendarClock className="h-3 w-3" /> {b.date} alle {b.time} · {b.duration} min
              </p>
            </div>
            <Badge variant="outline" className={statusBadge(b.status)}>{b.status}</Badge>
          </div>

          <div className="flex flex-wrap gap-1">
            {services.map(s => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div className="inline-flex items-center gap-1 truncate">
              <MapPin className="h-3 w-3" /> {room?.name || "—"}
            </div>
            {structure && (
              <div className="inline-flex items-center gap-1 truncate">
                <Bell className="h-3 w-3" /> Refertazione: {structure.name}
              </div>
            )}
            {specialist && (
              <div className="col-span-2 truncate">Specialista: {specialist.name}</div>
            )}
          </div>

          {(b.status === "confirmed" || b.status === "pending" || b.status === "rescheduled") && (
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => openMove(b)} className="gap-1" data-testid={`booking-move-${b.id}`}>
                <CalendarClock className="h-3 w-3" /> Sposta
              </Button>
              <Button variant="outline" size="sm" className="gap-1 text-destructive hover:text-destructive" onClick={() => cancelBooking(b.id)} data-testid={`booking-cancel-${b.id}`}>
                <Trash2 className="h-3 w-3" /> Annulla
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const upcoming = bookings.filter(b => ["confirmed", "pending", "rescheduled"].includes(b.status));
  const past = bookings.filter(b => ["completed", "cancelled"].includes(b.status));

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/manager")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ClipboardList className="h-4 w-4 text-indigo-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Prenotazioni</span>
              <MDRBadge variant="admin" size="sm" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Gestione prenotazioni</h1>
            <p className="text-sm text-muted-foreground mt-1">{upcoming.length} attive · {past.length} archiviate</p>
          </div>
          <Button variant="premium" onClick={() => navigate("/booking?from=pharmacy_manager&pharmacyId=ph1")} className="gap-2" data-testid="bookings-new-btn">
            <Plus className="h-4 w-4" /> Nuova prenotazione
          </Button>
        </div>
      </div>

      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="bookings-tab-upcoming">Attive ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="past" data-testid="bookings-tab-past">Archivio ({past.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="grid gap-3 md:grid-cols-2">
          {upcoming.length === 0 && (
            <Card className="md:col-span-2 border-dashed">
              <CardContent className="p-8 text-center text-sm text-muted-foreground">Nessuna prenotazione attiva.</CardContent>
            </Card>
          )}
          {upcoming.map(renderBookingCard)}
        </TabsContent>
        <TabsContent value="past" className="grid gap-3 md:grid-cols-2">
          {past.length === 0 && (
            <Card className="md:col-span-2 border-dashed">
              <CardContent className="p-8 text-center text-sm text-muted-foreground">Nessuna prenotazione archiviata.</CardContent>
            </Card>
          )}
          {past.map(renderBookingCard)}
        </TabsContent>
      </Tabs>

      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sposta prenotazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nuova data</Label>
              <Input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)} data-testid="booking-move-date" />
            </div>
            <div>
              <Label>Nuovo orario</Label>
              <Input type="time" value={moveTime} onChange={(e) => setMoveTime(e.target.value)} data-testid="booking-move-time" />
            </div>
            <p className="text-xs text-muted-foreground">Il paziente riceverà notifica via email e SMS.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveTarget(null)}>Annulla</Button>
            <Button onClick={confirmMove} data-testid="booking-move-confirm">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerBookingsPage;
